import memoize from 'transmute/memoize';
import I18n from 'I18n';
// @ts-expect-error untyped import
import formatMediumMonthYear from 'I18n/utils/formatMediumMonthYear';
export const formatPeriodAndYear = (period, year) => `${period}-${year}`;
export const QUARTER = 'QUARTER';
export const MONTH = 'MONTH';
export const INITIAL_QUARTER = 1;
export const INITIAL_MONTH = 1;
const MONTHS_IN_QUARTER = 3;
const MONTHS_IN_YEAR = 12;
const QUARTERS_IN_YEAR = 4;
const TIME_PERIOD_OFFSET_INDEX = 0;

// modulo operator that accounts for:
// - negative numbers
// - 0 results instead return m value
// inspired by: https://stackoverflow.com/a/17323608
export const mod = (n, m) => (n % m + m) % m || m;

// gets month # in fiscal year, used to calculate quarter
export const getMonthOfFiscalYear = ({
  month,
  offset
}) => mod(month - offset, MONTHS_IN_YEAR);

// gets current fiscal year (denoted by calendar year of last month of the fiscal year)
// e.g. if FY is Feb '21 - Jan '22, returns 2022
export const getCurrentFiscalYear = ({
  currentMonth,
  currentYear,
  offset = 0
}) => offset === 0 || currentMonth <= offset ? currentYear : currentYear + 1;

// gets fiscal quarter based on calendar month and offset
export const getQuarterForMonth = memoize(({
  month,
  offset = 0
}) => Math.ceil(getMonthOfFiscalYear({
  month,
  offset
}) / MONTHS_IN_QUARTER));

// based on a fiscal year (as defined above), calendar month and fiscal offset,
// derive which year should be displayed when formatting that month
export const deriveYearForMonthAndFiscalOffset = ({
  fiscalYear,
  month,
  offset
}) => offset === 0 || month <= offset ? fiscalYear : fiscalYear - 1;
export const getLastMonthOfQuarter = ({
  quarter,
  offset
}) => mod(quarter * MONTHS_IN_QUARTER + offset, MONTHS_IN_YEAR);

// subtracting 2 months from last month of quarter will get the first month of quarter
export const getFirstMonthOfQuarter = ({
  quarter,
  offset
}) => mod(getLastMonthOfQuarter({
  quarter,
  offset
}) - (MONTHS_IN_QUARTER - 1), MONTHS_IN_YEAR);
export const getFormattedMonth = ({
  month,
  year
}) => {
  // moment is set to 2nd day of the month to avoid displaying the previous month for positive UTC timezones
  const moment = I18n.moment(`2-${month}-${year}`, 'D-M-YYYY');
  return year ? formatMediumMonthYear(moment, 'portalTz') : I18n.moment.months(mod(month - 1, MONTHS_IN_YEAR));
};
export const getFormattedQuarterRange = ({
  quarter,
  fiscalYear,
  offset
}) => {
  const firstMonthOfQuarter = getFirstMonthOfQuarter({
    quarter,
    offset
  });
  const firstMonthOfQuarterYear = deriveYearForMonthAndFiscalOffset({
    month: firstMonthOfQuarter,
    offset,
    fiscalYear
  });
  const lastMonthOfQuarter = getLastMonthOfQuarter({
    quarter,
    offset
  });
  const lastMonthOfQuarterYear = deriveYearForMonthAndFiscalOffset({
    month: lastMonthOfQuarter,
    offset,
    fiscalYear
  });
  const formattedFirstMonthOfQuarter = getFormattedMonth({
    month: firstMonthOfQuarter,
    year: firstMonthOfQuarterYear === lastMonthOfQuarterYear ?
    // null because we don't need to show year twice if it's the same
    // for first and final months
    null : firstMonthOfQuarterYear
  });
  const formattedLastMonthOfQuarter = getFormattedMonth({
    month: lastMonthOfQuarter,
    year: lastMonthOfQuarterYear
  });
  return `${formattedFirstMonthOfQuarter} - ${formattedLastMonthOfQuarter}`;
};
const currentDate = new Date();
export const currentYear = currentDate.getFullYear();
const currentMonth = currentDate.getMonth() + 1;
const currentQuarter = getQuarterForMonth({
  month: currentMonth
});
export const getLocalDateNow = () => I18n.moment['portalTz'](new Date().toString()).format('YYYY-MM-DD');
export const deriveCurrentTimePeriodForCadence = cadence => cadence === QUARTER ? currentQuarter : currentMonth;
export const getOffset = ({
  timePeriodType,
  timePeriod,
  year,
  fiscalOffset = 0
}) => {
  const currDate = new Date();
  if (!timePeriodType || !timePeriod || !year) {
    return 0;
  }
  if (timePeriodType === MONTH) {
    let offset = timePeriod - (currDate.getMonth() + 1);
    if (year > currDate.getFullYear()) {
      offset += MONTHS_IN_YEAR;
    }
    if (year < currDate.getFullYear()) {
      offset -= MONTHS_IN_YEAR;
    }
    return offset;
  }
  const currQuarter = getQuarterForMonth({
    month: currDate.getMonth() + 1,
    offset: fiscalOffset
  });
  const currentFiscalYear = getCurrentFiscalYear({
    currentMonth: currDate.getMonth() + 1,
    currentYear: currDate.getFullYear(),
    offset: fiscalOffset
  });
  let quarterOffset = timePeriod - currQuarter;
  if (year > currentFiscalYear) {
    quarterOffset += QUARTERS_IN_YEAR;
  }
  if (year < currentFiscalYear) {
    quarterOffset -= QUARTERS_IN_YEAR;
  }
  return quarterOffset;
};
export const deriveTimePeriodValueAndYear = timePeriod => {
  const timePeriodValue = timePeriod.split('-')[0];
  const timePeriodYear = timePeriod.split('-')[1];
  return {
    timePeriodValue,
    timePeriodYear
  };
};
export const getTimePeriodOffset = timePeriod => {
  const offsetString = timePeriod.split('_')[TIME_PERIOD_OFFSET_INDEX];
  switch (offsetString) {
    case 'THIS':
      return 0;
    case 'NEXT':
      return 1;
    case 'LAST':
      return -1;
    default:
      return 0;
  }
};